// #3704 — the Claude Design round trip, guided from the UX Design agent's chat.
//
// The fixed text lives here, once, so the server-appended offer (apps/api routes/sessions.ts),
// the UX agent's prompt section (apps/api services/claudeDesignRoundTrip.ts) and the chat card
// (apps/web components/chat/ClaudeDesignRoundTripCard.tsx) can never say different things.
// AVR rulings: issue #3704, comment 6400159787 (ruling 8: fixed setup and handoff steps).

/** What the optional step adds, and that skipping it changes nothing. */
export const CLAUDE_DESIGN_OFFER_INTRO =
  'Optional, before Sprint Planning: refine these designs in Claude Design. ' +
  'Claude Design turns your approved Screen Specifications and Design Tokens into refined visual components and a design brief, ' +
  'and its handoff bundle brings them back into BluePrint. ' +
  'Skipping this step changes nothing: the handoff to Sprint Planning works exactly as it does without it.';

/** Setting up Claude Design (comment 6357397319, item 3). */
export const CLAUDE_DESIGN_SETUP_STEPS: readonly string[] = [
  'Export the ZIP. It contains PROMPT.md, README.md, screen-context.md and design-system/tokens.json.',
  'In Claude Design, start a new project and set Design system to None, so your organisation\'s default design system does not restyle this app.',
  'Choose UI mockups.',
  'Attach screen-context.md, README.md and the files in design-system/.',
  'Optional: add one line naming only the screens you want designed, to save tokens.',
  'Paste the contents of PROMPT.md and run it.',
];

/** Bringing the result back (comment 6357397319, items 5 and 6). */
export const CLAUDE_DESIGN_HANDOFF_STEPS: readonly string[] = [
  'Refine the designs in Claude Design. Check the business rules it had to guess.',
  'Download the handoff ZIP: Share → Claude Code → ▾ → download the handoff ZIP. Not Copy prompt, and not Project HTML: the import needs README.md and PROMPT.md at the top level of the ZIP.',
  'Upload that ZIP here. BluePrint checks it and the agent confirms what arrived; a Product Manager then approves it with Approve & Merge.',
];

/** The fixed message the card's Skip button sends. */
export const CLAUDE_DESIGN_SKIP_MESSAGE = 'Skip Claude Design and continue to Sprint Planning.';

/** The fixed message the card sends after a successful in-chat upload (ruling 5). */
export const CLAUDE_DESIGN_IMPORTED_MESSAGE = 'I have imported the Claude Design handoff bundle.';

/**
 * The exact sentence the UX agent says to ask for a seed bundle (designSystemSource =
 * claude_design_bundle). The chat shows the upload card when an agent reply contains it.
 */
export const CLAUDE_DESIGN_UPLOAD_REQUEST = 'Please upload your Claude Design bundle to continue.';

/** Heading of the server-appended offer; also how the strip below finds it. */
export const CLAUDE_DESIGN_OFFER_HEADING = '**Optional: Claude Design**';

/**
 * The offer as appended by the server to the reply whose approval completed all five Phase 2.5
 * artefacts (ruling 2). Fixed text only.
 */
export function claudeDesignOfferChatText(): string {
  const setup = CLAUDE_DESIGN_SETUP_STEPS.map((s, i) => `${i + 1}. ${s}`).join('\n');
  const back = CLAUDE_DESIGN_HANDOFF_STEPS.map((s, i) => `${i + 1}. ${s}`).join('\n');
  return (
    `${CLAUDE_DESIGN_OFFER_HEADING}\n\n${CLAUDE_DESIGN_OFFER_INTRO}\n\n` +
    `Set up Claude Design:\n${setup}\n\n` +
    `Bring it back:\n${back}\n\n` +
    'Use the Claude Design card below, or choose Skip. You can also ask me for refinement hints: the business rules in your specifications that Claude Design would otherwise guess.'
  );
}
